import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";

import {
  TrendingUp as SalesIcon,
  LocalDining as DineInIcon,
  TakeoutDining as PickUpIcon,
  DeliveryDining as DeliveryIcon,
  CardGiftcard as ComplimentaryIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import { useAuth } from '../../hooks/useAuth.js';
import { useAppStore } from '../../store/appStore';
import { vendorDashboardService } from '../../services/vendorDashboardService.js';
import SalesBarChart from '../../components/common/charts/SalesBarChart';
import SalesPieChart from '../../components/common/charts/SalesPieChart';
import SalesAreaChart from '../../components/common/charts/SalesAreaChart';
import StatCard from '../../components/common/StatCard';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branch: selectedBranch } = useAppStore();
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [fromTime, setFromTime] = useState('06:00');
  const [toTime, setToTime] = useState('17:00');
  const [appliedFilters, setAppliedFilters] = useState({ startDate: today, endDate: today, fromTime: '06:00', toTime: '17:00' });
  const [chartType, setChartType] = useState('bar');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [complimentaryBillsCount, setComplimentaryBillsCount] = useState(0);
  const [salesBySlot, setSalesBySlot] = useState([]);
  const [statsError, setStatsError] = useState(null);
  const complimentaryCountRequestId = useRef(0);

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    return `₹${numericValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchDashboardStats = useCallback(async () => {
    if (!user) return;

    setStatsLoading(true);
    setStatsError(null);
    try {
      const branchId = selectedBranch?.isAllOutlets || selectedBranch?.id === 'all-outlets'
        ? 'all'
        : selectedBranch?.id || 'all';

      const countRequestId = complimentaryCountRequestId.current + 1;
      complimentaryCountRequestId.current = countRequestId;
      setComplimentaryBillsCount(0);
      const complimentaryBillsPromise = vendorDashboardService.getVendorComplimentaryBills(
        appliedFilters.startDate,
        appliedFilters.endDate,
        branchId,
      ).then((response) => {
        const results = response?.data?.data?.results;
        if (!response?.data?.success || !Array.isArray(results)) {
          throw new Error(response?.data?.message || 'Invalid complimentary bills response');
        }
        if (complimentaryCountRequestId.current === countRequestId) {
          setComplimentaryBillsCount(results.length);
        }
      }).catch((error) => {
        if (complimentaryCountRequestId.current === countRequestId) {
          setComplimentaryBillsCount(0);
        }
        console.error('Error fetching vendor complimentary bills count:', error);
      });

      const [summaryResponse, salesResponse] = await Promise.all([
        vendorDashboardService.getVendorDashboardSummary(appliedFilters.startDate, appliedFilters.endDate, branchId, appliedFilters.fromTime, appliedFilters.toTime),
        vendorDashboardService.getVendorSalesPerformance(appliedFilters.startDate, appliedFilters.endDate, branchId, appliedFilters.fromTime, appliedFilters.toTime),
      ]);
      await complimentaryBillsPromise;

      if (summaryResponse.data.success) {
        setDashboardStats(summaryResponse.data.data);
      }

      if (salesResponse.data.success) {
        setSalesBySlot(salesResponse.data.data.salesBySlot || []);
      }
    } catch (error) {
      setStatsError(error.response?.data?.message || 'Unable to load dashboard data. Please try again.');
      console.error('Error fetching vendor dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [appliedFilters, selectedBranch, user]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    }
  }, [fetchDashboardStats, user]);

  const handleRefresh = () => {
    setLoading(true);
    fetchDashboardStats().finally(() => setLoading(false));
  };

  const handleApplyFilters = () => {
    if (startDate > endDate || (fromTime && !toTime) || (!fromTime && toTime) || (fromTime && toTime && fromTime > toTime)) {
      setStatsError('Please provide a valid date and time range.');
      return;
    }
    setAppliedFilters({ startDate, endDate, fromTime, toTime });
  };

  const vendorStats = useMemo(() => {
    const baseStats = dashboardStats || {};

    return {
      totalSales: {
        amount: formatCurrency(baseStats.totalSales),
        orders: baseStats.totalOrders,
        icon: SalesIcon,
        color: '#1976d2',
      },
      complimentaryBills: {
        count: complimentaryBillsCount,
        icon: ComplimentaryIcon,
        color: '#ef6c00',
      },
      dineIn: {
        amount: formatCurrency(baseStats.dineInSales),
        orders: baseStats.dineInOrders,
        icon: DineInIcon,
        color: '#1976d2',
      },
      pickUp: {
        amount: formatCurrency(baseStats.pickUpSales),
        orders: baseStats.pickUpOrders,
        icon: PickUpIcon,
        color: '#00897b',
      },
      delivery: {
        amount: formatCurrency(baseStats.deliverySales),
        orders: baseStats.deliveryOrders,
        icon: DeliveryIcon,
        color: '#6a1b9a',
      },
      netAmount: formatCurrency(baseStats.netAmount),
      totalGst: formatCurrency(baseStats.totalGst),
    };
  }, [dashboardStats, complimentaryBillsCount]);

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        maxWidth: '100%',
        width: '100%',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Vendor Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's your business overview
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="Start Date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="End Date" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="From Time" type="time" value={fromTime} onChange={(event) => setFromTime(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="To Time" type="time" value={toTime} onChange={(event) => setToTime(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <Button variant="contained" size="small" onClick={handleApplyFilters}>Apply</Button>
        </Box>
      </Box>

      {statsError ? (
        <Alert severity="error" onClose={() => setStatsError(null)} sx={{ mb: 3 }} action={<IconButton size="small" color="inherit" onClick={handleRefresh}><RefreshIcon fontSize="small" /></IconButton>}>
          {statsError}
        </Alert>
      ) : null}

      {loading || statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      ) : null}

      {!statsError && !statsLoading ? <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: { xs: 1.5, sm: 2, md: 2.5 }, mb: 4 }}>
        <StatCard title="Total Sales" value={vendorStats.totalSales.amount} icon={SalesIcon} color="#1976d2" />
        <StatCard title="Net Amount" value={vendorStats.netAmount} icon={SalesIcon} color="#1565c0" />
        <StatCard title="Total GST" value={vendorStats.totalGst} icon={SalesIcon} color="#ef6c00" />
        <StatCard title="Total Orders" value={vendorStats.totalSales.orders} icon={SalesIcon} color="#388e3c" />
        <Box
          role="button"
          tabIndex={0}
          aria-label="Complimentary Bills"
          onClick={() => navigate('/vendor/complimentary-bills')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate('/vendor/complimentary-bills');
            }
          }}
        >
          <StatCard title="Complimentary Bills" value={vendorStats.complimentaryBills.count} icon={ComplimentaryIcon} color="#ef6c00" />
        </Box>
        <StatCard title="Dine In Sales" value={vendorStats.dineIn.amount} icon={DineInIcon} color="#f57c00" />
        <StatCard title="Pick Up Sales" value={vendorStats.pickUp.amount} icon={PickUpIcon} color="#7b1fa2" />
        <StatCard title="Delivery Sales" value={vendorStats.delivery.amount} icon={DeliveryIcon} color="#6a1b9a" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', minWidth: 0 }}>
        <Paper sx={{ gridColumn: '1 / -1', width: '100%', minWidth: 0, minHeight: { xs: 430, sm: 550, md: 650 }, p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Sales Performance</Typography>
                <Typography variant="body2" color="text.secondary">Time slot analysis</Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                  <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                    <MenuItem value="bar">Bar Chart</MenuItem>
                    <MenuItem value="pie">Pie Chart</MenuItem>
                    <MenuItem value="area">Area Chart</MenuItem>
                  </Select>
                </FormControl>

                <IconButton onClick={handleRefresh} color="primary" disabled={loading || statsLoading} title="Refresh data" size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#1976d2' }} />
                <Typography variant="body2">Dine In</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#00897b' }} />
                <Typography variant="body2">Pick Up</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#6a1b9a' }} />
                <Typography variant="body2">Delivery</Typography>
              </Box>
            </Box>

            <Box sx={{ width: '100%', minWidth: 0, minHeight: { xs: 320, sm: 420, md: 520 }, height: { xs: 320, sm: 420, md: 520 }, '& .recharts-cartesian-grid line': { stroke: '#e0e0e0' }, '& .recharts-cartesian-axis-line': { stroke: '#9e9e9e' }, '& .recharts-cartesian-axis-tick-value': { fill: '#666', fontSize: 12 } }}>
              {salesBySlot.length === 0 ? <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography color="text.secondary">No sales data available for the selected period.</Typography></Box> : null}
              {salesBySlot.length > 0 && chartType === 'bar' && <SalesBarChart data={salesBySlot} />}
              {salesBySlot.length > 0 && chartType === 'pie' && <SalesPieChart data={salesBySlot} />}
              {salesBySlot.length > 0 && chartType === 'area' && <SalesAreaChart data={salesBySlot} />}
            </Box>
          </Paper>
      </Box>
      </> : null}
    </Box>
  );
};

export { VendorDashboard };
